import { ViewboxService } from '@adaptiva/widgets/viewbox';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { LlmConversationNode, LlmHistoryNode } from '../../classes';
import { ILlmConversation } from '../../interfaces';
import { AidaService } from '../aida';
import { LlmClient } from '../llm-client';

@Injectable()
export class LlmHistoryService extends LlmClient {

	public readonly aida: AidaService = inject(AidaService);

	public readonly viewbox: ViewboxService = inject(ViewboxService);

	public readonly tree: Signal<LlmHistoryNode | null> = computed(() => {
		const _root: LlmConversationNode = this.aida.conversationTree();
		const _conversation: ILlmConversation = this.aida.conversation();

		if (!_root)
			return null;

		return new LlmHistoryNode(_root as Partial<LlmHistoryNode>);
	});

	public readonly nodes: Signal<Map<string, LlmHistoryNode>> = computed(() => {
		return this.generateNodeMap(this.tree());
	});

	public deleteNode(_node: LlmConversationNode): Observable<void> {
		if (!_node)
			return throwError(() => new Error('No targeted node to delete'));

		const _url: string = `${this.getBaseUrl()}/delete-node`;

		return this.put(
			_url,
			{
				conversationId: this.aida.conversation().objectId,
				nodeId: _node.nodeId
			}
		).pipe(
			tap(() => {
				this.aida.removeConversationNode(_node.nodeId);
			})
		);
	}

	public generateNodeMap(
		_node: LlmHistoryNode,
		_map: Map<string, LlmHistoryNode> = new Map()
	): Map<string, LlmHistoryNode> {
		if (_node == null)
			return _map;

		for (const _child of _node.children)
			this.generateNodeMap(_child, _map);

		return _map.set(_node.nodeId, _node);
	}

	public generateDisplayTree(_node: LlmHistoryNode, _collapsed: Set<string>): LlmHistoryNode {
		if (!_node)
			return null;

		LlmHistoryNode.setCollapsed(_node, _collapsed, true);
		LlmHistoryNode.setPosition(_node, 0, 0);

		return _node;
	}

	public setViewboxTarget(_targetNodeId: string): void {
		const _target: string = `[data-testid=ada-llm-node-${_targetNodeId}]`;

		if (_target !== this.viewbox.targetSelector())
			this.viewbox.targetSelector.set(_target);
	}

}
